/**
 * @file departmentSlice.js
 * @description Sets initial state for the departments module. Creates reducers and actions for API Calls
 * @author Ben Yee
 */
import { createSlice } from '@reduxjs/toolkit';
import { deleteDepartment, getDepartments, saveDepartment } from '../../services/departmentService';
import { fetchDepartments as fetchAppDepartments } from 'app/appSlice';
import { logger } from 'utils/logger';

export const initialDepartmentFilter = {
    keyword: '',
    eventNames: [],
    productCategories: [],
    productSubcategories: [],
    groupBy: '',
};

const sortDepartments = (departments) => [...departments].sort((a, b) => a.deptSort.localeCompare(b.deptSort));

const departmentSlice = createSlice({
    name: 'department',
    initialState: {
        departments: [],
        selectedDepartment: null,
        selectedDepartments: [],
        departmentFilter: { ...initialDepartmentFilter },

        // Meta
        error: null,
        success: null,
        loading: false,
    },
    reducers: {
        selectDepartment(state, action) {
            state.selectedDepartment = action.payload ? { ...action.payload } : null;
        },
        selectDepartments(state, action) {
            state.selectedDepartments = action.payload ? [...action.payload] : [];
        },
        setDepartmentFilter(state, action) {
            state.departmentFilter = action.payload ? { ...action.payload } : { ...initialDepartmentFilter };
        },
        clearNotifications(state) {
            state.error = null;
            state.success = null;
        },

        //////////////////////
        //
        // GET DEPARTMENTS
        //
        //////////////////////
        getDepartmentsStart(state) {
            state.loading = true;
            state.success = null;
            state.error = null;
        },
        getDepartmentsSuccess(state, action) {
            state.loading = false;
            state.success = { type: action.type, message: 'Departments received' };
            state.departments = [...action.payload];
            state.error = null;
        },
        getDepartmentsFailure(state, action) {
            state.loading = false;
            state.error = action.payload;
        },

        //////////////////////
        //
        // SAVE DEPARTMENT (create or update)
        //
        //////////////////////
        saveDepartmentStart(state) {
            state.success = null;
            state.error = null;
        },
        saveDepartmentSuccess(state, action) {
            const department = action.payload;
            const idx = state.departments.findIndex((val) => val.deptSort === department.deptSort);
            const departments = [...state.departments];

            if (idx > -1) {
                departments[idx] = { ...departments[idx], ...department };
            } else {
                departments.push(department);
            }

            state.departments = sortDepartments(departments);
            state.success = { type: action.type, message: idx > -1 ? 'Department updated' : 'Department created' };
            state.error = null;
        },
        saveDepartmentFailure(state, action) {
            state.error = action.payload;
        },

        //////////////////////
        //
        // DELETE DEPARTMENT
        //
        //////////////////////
        deleteDepartmentStart(state) {
            state.success = null;
            state.error = null;
        },
        deleteDepartmentSuccess(state, action) {
            state.departments = state.departments.filter((val) => val.deptSort !== action.payload.deptSort);
            state.selectedDepartments = state.selectedDepartments.filter((val) => val.id !== action.payload.deptSort);
            state.success = { type: action.type, message: 'Department deleted' };
            state.error = null;
        },
        deleteDepartmentFailure(state, action) {
            state.error = action.payload;
        },

        //////////////////////
        //
        // BATCH UPDATE / DELETE
        //
        //////////////////////
        batchSaveDepartmentsSuccess(state, action) {
            const departments = [...state.departments];
            action.payload.forEach((department) => {
                const idx = departments.findIndex((val) => val.deptSort === department.deptSort);
                if (idx > -1) {
                    departments[idx] = { ...departments[idx], ...department };
                } else {
                    departments.push(department);
                }
            });

            state.departments = sortDepartments(departments);
            state.success = { type: action.type, message: `${action.payload.length} ${action.payload.length === 1 ? 'department' : 'departments'} updated` };
            state.error = null;
        },
        batchDeleteDepartmentsSuccess(state, action) {
            const deleted = action.payload.map((val) => val.deptSort);
            state.departments = state.departments.filter((val) => !deleted.includes(val.deptSort));
            state.selectedDepartments = state.selectedDepartments.filter((val) => !deleted.includes(val.id));
            state.success = { type: action.type, message: `${action.payload.length} ${action.payload.length === 1 ? 'department' : 'departments'} deleted` };
            state.error = null;
        },
    },
});

// DEPARTMENTS
export const fetchDepartments = () => async (dispatch) => {
    try {
        dispatch(getDepartmentsStart());
        const data = await getDepartments();
        dispatch(getDepartmentsSuccess(data));
    } catch (err) {
        logger.log('err', err);
        dispatch(getDepartmentsFailure(err));
    }
};

// DEPARTMENT CREATE / UPDATE
export const updateDepartment = (department) => async (dispatch) => {
    try {
        dispatch(saveDepartmentStart());
        const data = await saveDepartment(department);
        dispatch(saveDepartmentSuccess(data));
        // keep the app wide department list (used by selects and filters) in sync
        dispatch(fetchAppDepartments());
    } catch (err) {
        logger.log('err', err);
        dispatch(saveDepartmentFailure(err));
    }
};

// DEPARTMENT DELETE
export const removeDepartment = (department) => async (dispatch) => {
    try {
        dispatch(deleteDepartmentStart());
        const data = await deleteDepartment(department.deptSort);
        dispatch(deleteDepartmentSuccess(data));
        dispatch(fetchAppDepartments());
    } catch (err) {
        logger.log('err', err);
        dispatch(deleteDepartmentFailure(err));
    }
};

// BATCH UPDATE - the API has no bulk endpoint so each department is sent in turn
export const batchUpdateDepartments = (departments) => async (dispatch) => {
    const saved = [];
    try {
        dispatch(saveDepartmentStart());
        for (let i = 0; i < departments.length; i++) {
            saved.push(await saveDepartment(departments[i]));
        }
        dispatch(batchSaveDepartmentsSuccess(saved));
    } catch (err) {
        logger.log('err', err);
        // keep whatever saved before the failure
        if (saved.length) dispatch(batchSaveDepartmentsSuccess(saved));
        dispatch(saveDepartmentFailure(err));
    }
    dispatch(fetchAppDepartments());
};

// BATCH DELETE
export const batchRemoveDepartments = (departments) => async (dispatch) => {
    const deleted = [];
    try {
        dispatch(deleteDepartmentStart());
        for (let i = 0; i < departments.length; i++) {
            deleted.push(await deleteDepartment(departments[i].deptSort));
        }
        dispatch(batchDeleteDepartmentsSuccess(deleted));
    } catch (err) {
        logger.log('err', err);
        if (deleted.length) dispatch(batchDeleteDepartmentsSuccess(deleted));
        dispatch(deleteDepartmentFailure(err));
    }
    dispatch(fetchAppDepartments());
};

export const {
    selectDepartment,
    selectDepartments,
    setDepartmentFilter,
    clearNotifications,

    getDepartmentsStart,
    getDepartmentsSuccess,
    getDepartmentsFailure,

    saveDepartmentStart,
    saveDepartmentSuccess,
    saveDepartmentFailure,

    deleteDepartmentStart,
    deleteDepartmentSuccess,
    deleteDepartmentFailure,

    batchSaveDepartmentsSuccess,
    batchDeleteDepartmentsSuccess,
} = departmentSlice.actions;

export default departmentSlice.reducer;
